import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import {
  ButtonIcon,
  LoadingSpinner,
  ProductsAndPartsFilters,
  SearchBar,
  Table,
} from '@rabbit/elements/shared-components';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import {
  DTVendable,
  DTWarranty_Template,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import placeholderImg from '../../../assets/images/placeholder-image.png';
import {
  formatVendableDataForEditModal,
  generateFilterOptions,
  VendableEditorContext,
} from '@rabbit/bizproc/react';
import { MRT_PaginationState, MRT_SortingState } from 'material-react-table';
import { formatAndGetLastCategory } from 'apps/sage/src/utils/helpers';
import { useTranslation } from 'react-i18next';
import { ModalAddEditItemKind } from '../../organisms/ModalNewEditProduct/ModalAddEditItem';
import { AppContext } from '@rabbit/app-context';
import { useGetMySagePersonas } from '@rabbit/data/portal';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';

export interface ProductsTabProps {
  handleEditModal: (
    kind: ModalAddEditItemKind,
    title: string,
    itemDetails?: any
  ) => void;
}

interface FilterOptionShape {
  checkboxName: string;
  label: string;
  count: number;
}

interface FacetFilterShape {
  name: string;
  value: string;
}

interface AppliedFilterShape {
  category: string;
  query: string;
}

const centeredStyle = {
  display: 'flex',
  justifyContent: 'left',
  alignItems: 'center',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

export function ProductsTab({ handleEditModal }: ProductsTabProps) {
  /* ---------------------------------- Todos --------------------------------- */
  // TODO: Build the table data using the values in context
  // TODO: DEBOUNCE THE SHIT OUT OF THE TEXT SEARCH - DC
  // Look for the useDebounce hook in the code and replicate that here
  const { config } = useContext(AppContext);
  const contextValues = useContext(VendableEditorContext);
  const {
    vendableList,
    categoryList,
    searchParams,
    changeSearchParams,
    totalHits,
    totalPages,
    warrantyTemplates,
    warrantyTemplatesLoading,
    warrantyTemplatesError,
    shouldRefresh,
  } = contextValues;
  const { warrantorPersona } = useGetMySagePersonas();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = useState<FilterOptionShape[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilterShape>({
    category: '',
    query: '',
  });
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isResultsFiltered, setIsResultsFiltered] = useState(false);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const headerClassStyle = 'font-nunito font-bold text-[14px]';

  // Format the results
  const formatResultsForProductsTable = (results: DTVendable[]) => {
    const formattedResults = results.map((result, i) => {
      const templateTitle = warrantyTemplates?.find(
        (template) => template.docid === result.defaultTemplate
      )?.title;

      return {
        imageUrl: result?.img?.[0] ?? placeholderImg,
        isImageUploaded: !!result?.img?.[0],
        upc: result.upc,
        docid: result.docid,
        productImage: (
          <div
            style={{
              backgroundImage: `url(${result?.img?.[0] ?? placeholderImg})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              width: '75px',
              height: '75px',
              borderRadius: '20px',
              overflow: 'hidden',
            }}
          ></div>
        ),
        lastUpdated:
          result.tupdate || result.tcreate
            ? new Date(result.tupdate ?? result.tcreate)
            : '-',
        productName: result.title,
        sku: result.mpn,
        category:
          result?.category && Array.isArray(result?.category)
            ? formatAndGetLastCategory(result): '-',
        warrantyPlan: templateTitle ?? '-',
        deprecated: result.deprecated ? t('Yes') : t('No'),
        status: result?.published,
        actions: (
          <div className="flex justify-end gap-2 px-2 text-right">
            <PencilSquareIcon
              className="text-primary-900 h-[20px] w-[20px]"
              onClick={() =>
                handleEditModal(
                  ModalAddEditItemKind.products,
                  t('Edit product'),
                  formatVendableDataForEditModal(result)
                )
              }
            />
          </div>
        ),
      };
    });
    return formattedResults;
  };

  useEffect(() => {
    if (vendableList.length) {
      setFilterOptions(generateFilterOptions(categoryList ?? []));
    }
  }, [vendableList.length]);

  useEffect(() => {
    if (searchParams && changeSearchParams)
      changeSearchParams({
        searchQuery: appliedFilters.query,
        page: pagination.pageIndex,
        hitsPerPage: pagination.pageSize,
        //facetFilters: [{ name: 'categories', value: appliedFilters.category }],
      });
  }, [
    appliedFilters.query,
    pagination.pageIndex,
    pagination.pageSize,
    appliedFilters.category,
  ]);

  // if (!vendableList.length)
  //   return <LoadingSpinner size="md" extraClasses="mt-16" />;

  const handleFilterOptions = (filterLabel: string) => {
    setIsResultsFiltered(!isResultsFiltered);
    !isResultsFiltered
      ? setAppliedFilters({ category: filterLabel, query: '' })
      : setAppliedFilters({ category: '', query: '' });
  };

  const handleNavigation = (data: any) => {
    navigate(SAGE_ROUTE_NAME.PRODUCTS + '/' + data.data.docid, {
      state: { docid: data.data.docid, productName: data.data.productName },
    });
  };

  const cellValueRenderer = (value: any) => {
    return (
      <div className="w-full truncate rounded-sm px-2 py-0.5 text-base">
        {value}
      </div>
    );
  };

  const cellImageRenderer = (imageUrl: any, altName: string) => {
    return (
      <div className="h-16 w-16 overflow-hidden rounded-lg border-[1px] border-gray-200 object-cover">
        <img
          src={imageUrl}
          alt={altName}
          className="h-full w-full object-contain"
          width={75}
          height={75}
        />
      </div>
    );
  };

  const columns: any[] = [
    {
      header: t('general.productImage'),
      accessorKey: 'imageUrl',
      enableSorting: false,
      enableColumnFilter: false,
      filterVariant: 'select',
      filterSelectOptions: [t('Yes'), t('No')],
      accessorFn: (row: any) => (row.isImageUploaded ? t('Yes') : t('No')),
      Cell: ({ row }: any) =>
        cellImageRenderer(row.original.imageUrl, row.original.productName),
      size: 100,
    },
    {
      header: t('general.productName'),
      accessorKey: 'productName',
      enableColumnFilter: true,
      filterVariant: 'text',
      Cell: ({ row }: any) => cellValueRenderer(row.original.productName),
    },
    {
      header: t('general.category'),
      accessorKey: 'category',
      enableColumnFilter: true,
      filterVariant: 'select',
      filterSelectOptions: filterOptions.map((option) => option.label),
      Cell: ({ row }: any) => cellValueRenderer(row.original.category),
    },
    {
      header: t('SKU'),
      accessorKey: 'sku',
      enableColumnFilter: true,
      filterVariant: 'text',
      Cell: ({ row }: any) => cellValueRenderer(row.original.sku),
    },
    {
      header: t('general.warrantyPlan'),
      accessorKey: 'warrantyPlan',
      enableColumnFilter: true,
      filterVariant: 'select',
      filterSelectOptions: warrantyTemplates?.map((template) => template.title),
      Cell: ({ row }: any) => cellValueRenderer(row.original.warrantyPlan),
    },
    {
      header: t('general.deprecated'),
      accessorKey: 'deprecated',
      enableColumnFilter: true,
      filterVariant: 'select',
      filterSelectOptions: [t('Yes'), t('No')],
      Cell: ({ row }: any) => cellValueRenderer(row.original.deprecated),
    },
    {
      header: t('general.lastUpdated'),
      accessorKey: 'lastUpdated',
      filterVariant: 'date-range',
      muiFilterDatePickerProps: {
        format: 'DD/MM/YYYY',
      },
      sortingFn: 'datetime',
      accessorFn: (row: any) =>
        row.lastUpdated instanceof Date
          ? row.lastUpdated.getTime()
          : row.lastUpdated,
      Cell: ({ row }: any) => {
        const value = row.original.lastUpdated;
        if (value instanceof Date) {
          const day = value.getDate().toString().padStart(2, '0');
          const month = (value.getMonth() + 1).toString().padStart(2, '0');
          const year = value.getFullYear();
          return `${day}/${month}/${year}`;
        }
        return value;
      },
    },
    {
      header: t('general.status'),
      accessorKey: 'status',
      enableColumnFilter: true,
      filterVariant: 'select',
      filterSelectOptions: [t('Published'), t('Unpublished')],
      accessorFn: (row: any) =>
        row.status ? t('Published') : t('Unpublished'),
      Cell: ({ row }: any) => (
        <div className="flex w-full items-center justify-start rounded-sm py-0.5 text-base">
          {row.original?.status ? (
            <div className="w-auto overflow-hidden rounded bg-green-100 py-1 px-2">
              {t('Published')}
            </div>
          ) : (
            <div className="w-auto overflow-hidden rounded bg-green-100 py-1 px-2">
              {t('Unpublished')}
            </div>
          )}
        </div>
      ),
    },
    {
      header: t('general.actions'),
      accessorKey: 'actions',
      enableSorting: false,
      enableColumnFilter: false,
      size: 50,
      Cell: ({ row }: any) => (
        <div className="flex w-full items-center justify-start gap-2 text-right">
          <PencilSquareIcon
            className="text-primary-900 h-[25px] w-[25px]"
            onClick={(e) => {
              e.stopPropagation();
              handleEditModal(
                ModalAddEditItemKind.products,
                t('Edit product'),
                formatVendableDataForEditModal(row.original)
              );
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="flex flex-col">
        <div className="relative z-10 flex w-full items-center justify-end pb-4">
          {config.VENDABLES.EDITOR.CAN_ADD_NEW.includes(
            (warrantorPersona?.personaId?.[0] as PersonaTypeSingleLetter) ?? ''
          ) && (
            <div className="md:absolute md:right-5 md:top-[40px] xl:top-[40px]">
              <ButtonIcon
                className="absolute"
                label={t('Add product')}
                iconLeft={true}
                onClick={() =>
                  handleEditModal(
                    ModalAddEditItemKind.products,
                    t('New product')
                  )
                }
              />
            </div>
          )}
        </div>
        <div className="">
          <Table
            name="products-table"
            columns={columns}
            data={formatResultsForProductsTable(vendableList)}
            advancedTable={false}
            enableColumnFilters={true}
            enableColumnActions={true}
            enableSorting={true}
            manualSearch={true}
            enableTopToolbar={true}
            globalFilter={appliedFilters.query}
            enableRowSelection={true}
            columnFilterDisplayMode="popover"
            onGlobalFilterChange={(val: string) =>
              setAppliedFilters((prev) => ({ ...prev, query: val }))
            }
            manualPagination={true}
            rowCount={totalHits ?? vendableList.length}
            state={{ pagination, sorting }}
            onPaginationChange={setPagination}
            manualSorting={true}
            onSortChange={setSorting}
            initialState={{
              showGlobalFilter: true,
              sorting: [{ id: 'lastUpdated', desc: true }],
              columnPinning: { right: ['actions'] },
              columnVisibility: { deprecated: false },
              pagination: {
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
              },
            }}
            useCallbackClickRow={(row: any) =>
              navigate(SAGE_ROUTE_NAME.PRODUCTS + '/' + row.docid, {
                state: { docid: row.docid, productName: row.productName },
              })
            }
            enableToolbarInternalActions={false}
            positionToolbarAlertBanner="bottom"
          />
        </div>
      </div>
    </LocalizationProvider>
  );
}

export default ProductsTab;
